import { useState, useEffect } from "react";
import { C, Card, CardHead, SectionTitle, Btn, Badge, Modal, Field, statusBadge } from "./theme";
import { GET, POST, DELETE } from "./api";
import { useLang } from "./i18n";

export default function UserMyVMs({ me, vms, users, collabVms, metricsSummary, getServerIp, fetchAll }) {
  const { t } = useLang();
  const [loading, setLoading] = useState({});
  const [diskInfo, setDiskInfo] = useState(null);
  const [collabModal, setCollabModal] = useState(null);
  const [collabs, setCollabs] = useState({});
  const [collabUser, setCollabUser] = useState("");
  const [startModal, setStartModal] = useState(null);
  const [gpuAvail, setGpuAvail] = useState([]);
  const [startForm, setStartForm] = useState({ gpu_ids: "", reason: "", expected_end: "" });

  useEffect(() => {
    GET("/api/disk").then(setDiskInfo).catch(() => {});
    const id = setInterval(() => GET("/api/disk").then(setDiskInfo).catch(() => {}), 10000);
    return () => clearInterval(id);
  }, []);

  // Fetch collaborators for each VM
  useEffect(() => {
    const fetch = async () => {
      for (const vm of vms) {
        try {
          const data = await GET(`/api/vms/${vm.id}/collaborators`);
          setCollabs(prev => ({ ...prev, [vm.id]: data }));
        } catch (e) {}
      }
    };
    fetch();
  }, [vms]);

  const openStart = async (vm) => {
    setStartModal(vm);
    setStartForm({ gpu_ids: vm.gpu_ids || "", reason: "", expected_end: "" });
    try {
      const avail = await GET(`/api/gpu-available/${vm.server}`);
      setGpuAvail(avail);
    } catch (e) { setGpuAvail([]); }
  };

  const doStart = async () => {
    if (!startModal) return;
    const prevCount = (startModal.gpu_ids || "").split(",").filter(Boolean).length;
    const newCount = (startForm.gpu_ids || "").split(",").filter(Boolean).length;

    if (newCount < prevCount && prevCount > 0) {
      if (!window.confirm(`이전 GPU ${prevCount}개 → ${newCount}개로 줄어듭니다. 계속하시겠습니까?`)) return;
    }

    setLoading(p => ({ ...p, [startModal.id + "start"]: true }));
    try {
      await POST(`/api/vms/${startModal.id}/start`, {
        gpu_ids: startForm.gpu_ids || "",
        reason: startForm.reason,
        expected_end: startForm.expected_end,
      });
      setStartModal(null);
      await fetchAll();
    } catch (e) { alert(e.message); }
    setLoading(p => ({ ...p, [startModal.id + "start"]: false }));
  };

  const doStop = async (vmId) => {
    setLoading(p => ({ ...p, [vmId + "stop"]: true }));
    try {
      await POST(`/api/vms/${vmId}/stop`);
      await fetchAll();
    } catch (e) { alert(e.message); }
    setLoading(p => ({ ...p, [vmId + "stop"]: false }));
  };

  const addCollab = async (vmId) => {
    if (!collabUser) return;
    try {
      await POST(`/api/vms/${vmId}/collaborators`, { user_id: collabUser });
      const data = await GET(`/api/vms/${vmId}/collaborators`);
      setCollabs(prev => ({ ...prev, [vmId]: data }));
      setCollabUser("");
      alert("협업자 추가 완료");
    } catch (e) { alert(e.message); }
  };

  const removeCollab = async (vmId, userId) => {
    if (!window.confirm(`${userId}를 협업자에서 제거하시겠습니까?`)) return;
    try {
      await DELETE(`/api/vms/${vmId}/collaborators/${userId}`);
      const data = await GET(`/api/vms/${vmId}/collaborators`);
      setCollabs(prev => ({ ...prev, [vmId]: data }));
    } catch (e) { alert(e.message); }
  };

  const vmDisk = (vmId) => diskInfo?.vms?.find(d => d.vm_id === vmId);

  const renderVmCard = (vm, isOwner) => {
    const m = metricsSummary[vm.id];
    const gpuCount = vm.gpu_ids ? vm.gpu_ids.split(",").filter(Boolean).length : 0;
    const disk = vmDisk(vm.id);
    const vmStatus = vm.vm_status || vm.status;
    const vmCollabs = collabs[vm.id] || [];

    return (
      <Card key={vm.id}>
        <CardHead>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ fontSize: 13, fontFamily: "monospace", color: C.text, fontWeight: 700 }}>
              {vm.id}
            </span>
            {statusBadge(vmStatus)}
            {!isOwner && <Badge label={`${t("owned_by")}: ${vm.owner_name || vm.user_id}`} color={C.amber} />}
            {vm.vm_ip && <span style={{ fontSize: 9, color: C.dim2, fontFamily: "monospace" }}>{vm.vm_ip}</span>}
          </div>
          <div style={{ display: "flex", gap: 6 }}>
            {isOwner && <>
              <Btn small variant="green"
                disabled={vmStatus === "running" || loading[vm.id + "start"]}
                onClick={() => openStart(vm)}>
                {loading[vm.id + "start"] ? t("starting") : t("start")}
              </Btn>
              <Btn small variant="red"
                disabled={vmStatus !== "running" || loading[vm.id + "stop"]}
                onClick={() => doStop(vm.id)}>
                {loading[vm.id + "stop"] ? t("stopping") : t("stop")}
              </Btn>
            </>}
          </div>
        </CardHead>
        <div style={{ padding: "14px 16px" }}>
          <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginBottom: 14 }}>
            {[
              { l: t("server"), v: vm.server },
              { l: "SSH", v: `${getServerIp(vm.server)}:${vm.ssh_port}` },
            ].map(mi => (
              <span key={mi.l} style={{ fontSize: 10, color: C.dim2, fontFamily: "monospace" }}>
                {mi.l}: <span style={{ color: C.text }}>{mi.v}</span>
              </span>
            ))}
          </div>

          <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginBottom: 14, fontSize: 11, fontFamily: "monospace" }}>
            <span style={{ color: C.dim2 }}>CPU: <span style={{ color: C.blue, fontWeight: 700 }}>
              {m ? `${m.cpu_pct?.toFixed(1)}%` : `${vm.cpu}코어`}
            </span>{m && <span style={{ color: C.dim2 }}> / {vm.cpu}코어</span>}</span>
            <span style={{ color: C.dim2 }}>RAM: <span style={{ color: C.purple, fontWeight: 700 }}>
              {m && m.mem_total_mb > 0 ? `${(m.mem_used_mb/1024).toFixed(1)}G` : `${vm.ram}G`}
            </span> / {vm.ram}G</span>
            <span style={{ color: C.dim2 }}>GPU: <span style={{ color: C.green, fontWeight: 700 }}>
              {m && m.gpu_util > 0 ? `${m.gpu_util.toFixed(0)}%` : `${gpuCount}개`}
            </span>{vm.gpu_ids && ` (${vm.gpu_ids})`}</span>
            <span style={{ color: C.dim2 }}>DISK: <span style={{ color: C.cyan, fontWeight: 700 }}>
              {disk ? `${disk.used_gb}G` : "--"}
            </span> / {vm.disk}G</span>
            {m && m.gpu_temp > 0 && <>
              <span style={{ color: C.dim2 }}>VRAM: <span style={{ color: C.blue, fontWeight: 700 }}>
                {(m.gpu_mem_used_mb/1024).toFixed(1)}/{(m.gpu_mem_total_mb/1024).toFixed(0)}G
              </span></span>
              <span style={{ color: C.dim2 }}>TEMP: <span style={{
                color: m.gpu_temp>=75?C.red:m.gpu_temp>=60?C.amber:C.blue, fontWeight: 700
              }}>{m.gpu_temp.toFixed(0)}C</span></span>
              {m.gpu_power_w > 0 && <span style={{ color: C.dim2 }}>PWR: <span style={{
                color: C.amber, fontWeight: 700
              }}>{m.gpu_power_w.toFixed(0)}W</span></span>}
            </>}
          </div>

          {/* SSH */}
          <div style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: 6, padding: "8px 12px", marginBottom: 10 }}>
            <div style={{ fontSize: 9, color: C.dim2, marginBottom: 4 }}>{t("ssh_command")}</div>
            <span style={{ fontSize: 11, color: C.cyan, fontFamily: "monospace" }}>
              ssh -i {vm.id}.pem -p {vm.ssh_port} {me.id}@{getServerIp(vm.server)}
            </span>
          </div>

          {/* Collaborators */}
          {isOwner && (
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <span style={{ fontSize: 9, color: C.dim2 }}>{t("collaborators")}:</span>
              {vmCollabs.length === 0 && <span style={{ fontSize: 10, color: C.dim2 }}>{t("none")}</span>}
              {vmCollabs.map(c => (
                <Badge key={c.user_id} label={`${c.user_id} (${c.user_name})`} color={C.blue} />
              ))}
              <Btn small variant="ghost" onClick={() => { setCollabModal(vm.id); setCollabUser(""); }}>
                + {t("add")}
              </Btn>
            </div>
          )}
        </div>
      </Card>
    );
  };

  const noVms = (!vms || vms.length === 0) && (!collabVms || collabVms.length === 0);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {noVms && (
        <div style={{ padding: 40, textAlign: "center" }}>
          <div style={{ fontSize: 13, color: C.dim2, fontFamily: "monospace" }}>
            {t("no_assigned_vm")}
          </div>
        </div>
      )}

      {vms && vms.length > 0 && <>
        <SectionTitle title={t("my_vms")} />
        {vms.map(vm => renderVmCard(vm, true))}
      </>}

      {collabVms && collabVms.length > 0 && <>
        <SectionTitle title={t("shared_vms")} />
        {collabVms.map(vm => renderVmCard(vm, false))}
      </>}

      {/* Start VM modal */}
      {startModal && (
        <Modal title={`${t("start_vm")}: ${startModal.id}`} onClose={() => setStartModal(null)}>
          <div style={{ fontSize: 11, color: C.dim2, marginBottom: 14, fontFamily: "monospace" }}>
            {t("server")}: {startModal.server} | {t("prev_gpu")}: {startModal.gpu_ids || t("none")}
          </div>

          <div style={{ fontSize: 9, color: C.dim2, letterSpacing: 1, marginBottom: 8 }}>{t("select_gpu")}</div>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 14 }}>
            {gpuAvail.map(g => {
              const selected = startForm.gpu_ids.split(",").map(s => s.trim()).includes(String(g.gpu_id));
              const toggleGpu = () => {
                const current = startForm.gpu_ids.split(",").map(s => s.trim()).filter(Boolean);
                let next;
                if (selected) {
                  next = current.filter(x => x !== String(g.gpu_id));
                } else {
                  if (!g.available) return;
                  next = [...current, String(g.gpu_id)];
                }
                setStartForm({ ...startForm, gpu_ids: next.join(",") });
              };
              return (
                <div key={g.gpu_id} onClick={toggleGpu} style={{
                  padding: "8px 14px", borderRadius: 6, cursor: g.available ? "pointer" : "not-allowed",
                  opacity: g.available ? 1 : 0.4,
                  background: selected ? C.cyan + "22" : C.bg,
                  border: `1px solid ${selected ? C.cyan : g.available ? C.border : C.red + "44"}`,
                }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: selected ? C.cyan : C.text,
                    fontFamily: "monospace" }}>GPU {g.gpu_id}</div>
                  <div style={{ fontSize: 9, color: g.available ? C.green : C.red }}>
                    {g.available ? t("free") : `${g.used_by?.user_id} ${t("in_use")}`}
                  </div>
                </div>
              );
            })}
          </div>

          <Field label={t("gpu_direct_input")} value={startForm.gpu_ids}
            onChange={v => setStartForm({ ...startForm, gpu_ids: v })}
            placeholder={t("no_gpu_start")} />

          <Field label={t("usage_reason")} value={startForm.reason}
            onChange={v => setStartForm({ ...startForm, reason: v })}
            placeholder={t("usage_reason_ph")} />

          <Field label={t("expected_end")} value={startForm.expected_end}
            onChange={v => setStartForm({ ...startForm, expected_end: v })}
            placeholder={t("expected_end_ph")} />

          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 16 }}>
            <Btn variant="ghost" onClick={() => setStartModal(null)}>{t("cancel")}</Btn>
            <Btn variant="green" disabled={loading[startModal.id + "start"]}
              onClick={doStart}>
              {loading[startModal.id + "start"] ? t("starting") : t("start")}
            </Btn>
          </div>
        </Modal>
      )}

      {/* Add collaborator modal */}
      {collabModal && (
        <Modal title={`${t("add_collaborator")}: ${collabModal}`} onClose={() => setCollabModal(null)}>
          <div style={{ fontSize: 12, color: C.text, marginBottom: 12 }}>
            {t("collab_desc")}
          </div>
          <Field label={t("users")} value={collabUser}
            onChange={v => setCollabUser(v)} placeholder={t("collab_user_id")} />

          {/* Current collaborators */}
          {(collabs[collabModal] || []).length > 0 && (
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 9, color: C.dim2, marginBottom: 6, letterSpacing: 1 }}>{t("current_collabs")}</div>
              {(collabs[collabModal] || []).map(c => (
                <div key={c.user_id} style={{ display: "flex", justifyContent: "space-between",
                  alignItems: "center", padding: "4px 0" }}>
                  <span style={{ fontSize: 11, color: C.text, fontFamily: "monospace" }}>
                    {c.user_id} ({c.user_name})
                  </span>
                  <Btn small variant="red" onClick={() => removeCollab(collabModal, c.user_id)}>{t("remove")}</Btn>
                </div>
              ))}
            </div>
          )}

          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 16 }}>
            <Btn variant="ghost" onClick={() => setCollabModal(null)}>{t("close")}</Btn>
            <Btn variant="cyan" onClick={() => addCollab(collabModal)} disabled={!collabUser}>{t("add")}</Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}
